import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    CloseTemplatePathParamsSchema,
    CloseTemplateIdPathParamsSchema,
    CloseTemplateListQuerySchema,
    CloseTemplateCreateSchema,
    CloseTemplateUpdateSchema,
    CloseTemplateApplySchema,
    CloseTemplateApplyResponseSchema,
    CloseTemplatePreviewResponseSchema,
    CloseTemplateResolveTaskViewSchema,
    CloseTemplateResolveTaskViewResponseSchema,
    DeleteCloseTemplateResultSchema,
    ArchiveCloseTemplateResultSchema,
} from "../schemas/close-template.schema";
import {
    CloseTemplateSchema,
    CloseTemplateWithStatsSchema,
} from "../types/closeTemplate";

export {
    CloseTemplateCreateSchema,
    CloseTemplateUpdateSchema,
    CloseTemplateApplySchema,
    CloseTemplateResolveTaskViewSchema,
    CloseTemplateResolveTaskViewResponseSchema,
};

export type {
    CloseTemplateCreate,
    CloseTemplateUpdate,
    CloseTemplateApplyResponse,
    CloseTemplatePreviewResponse,
    CloseTemplateResolveTaskView,
    CloseTemplateResolveTaskViewResponse,
} from "../schemas/close-template.schema";

const c = initContract();

export const closeTemplateContract = c.router(
    {
        listCloseTemplates: {
            method: "GET",
            path: "/organizations/:organizationId",
            pathParams: CloseTemplatePathParamsSchema,
            query: CloseTemplateListQuerySchema,
            responses: {
                200: z.object({
                    closeTemplates: z.array(CloseTemplateWithStatsSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCloseTemplate: {
            method: "GET",
            path: "/organizations/:organizationId/:closeTemplateId",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            responses: {
                200: CloseTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: CloseTemplatePathParamsSchema,
            body: CloseTemplateCreateSchema,
            responses: {
                201: CloseTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCloseTemplate: {
            method: "PATCH",
            path: "/organizations/:organizationId/:closeTemplateId",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: CloseTemplateUpdateSchema,
            responses: {
                200: CloseTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                422: ErrorMessageSchema,
            },
        },
        deleteCloseTemplate: {
            method: "DELETE",
            path: "/organizations/:organizationId/:closeTemplateId",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            responses: {
                200: DeleteCloseTemplateResultSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        duplicateCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:closeTemplateId/duplicate",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                201: CloseTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        applyCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:closeTemplateId/apply",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: CloseTemplateApplySchema,
            responses: {
                201: CloseTemplateApplyResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        resolveTaskView: {
            method: "POST",
            path: "/organizations/:organizationId/resolve-task-view",
            pathParams: CloseTemplatePathParamsSchema,
            body: CloseTemplateResolveTaskViewSchema,
            responses: {
                200: CloseTemplateResolveTaskViewResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        archiveCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:closeTemplateId/archive",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ArchiveCloseTemplateResultSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unarchiveCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:closeTemplateId/unarchive",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ArchiveCloseTemplateResultSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        previewCloseTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:closeTemplateId/preview",
            pathParams: CloseTemplatePathParamsSchema.merge(
                CloseTemplateIdPathParamsSchema,
            ),
            body: CloseTemplateApplySchema,
            responses: {
                200: CloseTemplatePreviewResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/close-templates",
    },
);
